import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Archive, ArchiveRestore, Trash2 } from 'lucide-react';
import type { PayPlanTemplate, PayType } from '@pay-plan/shared';
import { GrossEntryMode, UserRole } from '@pay-plan/shared';
import { useApi } from '../hooks/use-api.js';
import { useAuth } from '../context/AuthContext.js';
import {
  listTemplates,
  updateTemplate,
  deleteTemplate,
  getTemplateAssignments,
} from '../api/templates.js';
import { listUsers } from '../api/users.js';
import { userDisplayNameMap, formatUserName } from '../utils/user-display.js';
import { LoadingSpinner } from '../components/LoadingSpinner.js';
import { EmptyState } from '../components/EmptyState.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { ConfirmDialog } from '../components/ConfirmDialog.js';

const PAY_TYPE_LABELS: Record<PayType, string> = {
  salaried: 'Salaried',
  commissioned: 'Commissioned',
  hourly: 'Hourly',
  hybrid: 'Hybrid',
};

const GROSS_MODE_LABELS: Record<GrossEntryMode, string> = {
  [GrossEntryMode.Shared]: 'Shared',
  [GrossEntryMode.SharedAdjusted]: 'Shared & Adjusted',
  [GrossEntryMode.Independent]: 'Independent',
  [GrossEntryMode.None]: 'None',
};

type StatusFilter = 'active' | 'all' | 'draft' | 'published' | 'archived';

export function TemplateListPage(): React.JSX.Element {
  const { data: templates, loading, error, refetch } = useApi(listTemplates, []);
  const { data: users } = useApi(listUsers, []);
  const userMap = useMemo(() => userDisplayNameMap(users), [users]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [archiveTarget, setArchiveTarget] = useState<PayPlanTemplate | null>(null);
  const [unarchiveTarget, setUnarchiveTarget] = useState<PayPlanTemplate | null>(null);
  const { role } = useAuth();
  // Only Admin/Executive can author templates. General Managers get a
  // read-only, rooftop-scoped view (the server enforces the scoping).
  const canManage = role === UserRole.Admin || role === UserRole.Executive;
  const canDelete = canManage;
  const canArchive = canManage;
  const [deleteTarget, setDeleteTarget] = useState<PayPlanTemplate | null>(null);
  const [deleteAssignmentCount, setDeleteAssignmentCount] = useState(0);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const filtered = useMemo(() => {
    if (templates === null) return [];
    return templates.filter((t) => {
      const matchesSearch =
        search === '' ||
        t.name.toLowerCase().includes(search.toLowerCase()) ||
        t.roleLabel.toLowerCase().includes(search.toLowerCase());
      let matchesStatus: boolean;
      if (statusFilter === 'active') {
        matchesStatus = t.status !== 'archived';
      } else if (statusFilter === 'all') {
        matchesStatus = true;
      } else {
        matchesStatus = t.status === statusFilter;
      }
      return matchesSearch && matchesStatus;
    });
  }, [templates, search, statusFilter]);

  async function handleArchive(): Promise<void> {
    if (archiveTarget === null) return;
    await updateTemplate(archiveTarget.templateId, { status: 'archived' });
    setArchiveTarget(null);
    refetch();
  }

  async function handleUnarchive(): Promise<void> {
    if (unarchiveTarget === null) return;
    await updateTemplate(unarchiveTarget.templateId, { status: 'published' });
    setUnarchiveTarget(null);
    refetch();
  }

  async function handleDeleteClick(template: PayPlanTemplate): Promise<void> {
    setDeleteLoading(true);
    try {
      const { count } = await getTemplateAssignments(template.templateId);
      setDeleteAssignmentCount(count);
      setDeleteTarget(template);
    } catch {
      setDeleteAssignmentCount(0);
      setDeleteTarget(template);
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (deleteTarget === null) return;
    const cascade = deleteAssignmentCount > 0;
    setDeleteError(null);
    try {
      await deleteTemplate(deleteTarget.templateId, cascade);
      setDeleteTarget(null);
      refetch();
    } catch (err: unknown) {
      setDeleteTarget(null);
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete template');
    }
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {error !== null && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700 mb-6">
          Failed to load templates: {error}
        </div>
      )}
      {deleteError !== null && (
        <div className="rounded-lg bg-red-50 p-4 text-sm text-red-700 mb-6">{deleteError}</div>
      )}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none"
          >
            <option value="active">Active</option>
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Inactive</option>
          </select>
        </div>
        {canManage && (
          <Link
            to="/templates/new"
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark transition-colors"
          >
            <Plus size={16} />
            New Template
          </Link>
        )}
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          title="No templates found"
          description={
            templates !== null && templates.length > 0
              ? 'Try adjusting your filters.'
              : 'Create your first pay plan template to get started.'
          }
          action={
            canManage && templates?.length === 0 ? (
              <Link
                to="/templates/new"
                className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark"
              >
                <Plus size={16} />
                New Template
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <Th>Name</Th>
                <Th>Role Label</Th>
                <Th>Department</Th>
                <Th>Pay Type</Th>
                <Th>Location</Th>
                <Th>Base Rate</Th>
                <Th>Gross Mode</Th>
                <Th>Kickers</Th>
                <Th>Status</Th>
                <Th>Published</Th>
                <Th>Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filtered.map((t) => (
                <TemplateRow
                  key={t.templateId}
                  template={t}
                  userMap={userMap}
                  onArchive={() => setArchiveTarget(t)}
                  onUnarchive={() => setUnarchiveTarget(t)}
                  onDelete={(tmpl) => {
                    void handleDeleteClick(tmpl);
                  }}
                  canArchive={canArchive}
                  canDelete={canDelete}
                  canEdit={canManage}
                  deleteLoading={deleteLoading}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        open={archiveTarget !== null}
        title="Make Template Inactive"
        message={`This will retire "${archiveTarget?.name ?? ''}". It will no longer appear in the active list but can still be viewed by filtering to Inactive. Existing pay sheets using this template are not affected.`}
        confirmLabel="Make Inactive"
        onConfirm={() => {
          void handleArchive();
        }}
        onCancel={() => setArchiveTarget(null)}
      />
      <ConfirmDialog
        open={unarchiveTarget !== null}
        title="Reactivate Template"
        message={`Bring "${unarchiveTarget?.name ?? ''}" back to Published status? It will reappear in the active list.`}
        confirmLabel="Reactivate"
        onConfirm={() => {
          void handleUnarchive();
        }}
        onCancel={() => setUnarchiveTarget(null)}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete Template"
        message={
          deleteAssignmentCount > 0
            ? `Permanently delete "${deleteTarget?.name ?? ''}" and its ${deleteAssignmentCount} active assignment(s)? This cannot be undone.`
            : `Permanently delete "${deleteTarget?.name ?? ''}"? This cannot be undone.`
        }
        confirmLabel={deleteAssignmentCount > 0 ? 'Delete All' : 'Delete'}
        variant="destructive"
        onConfirm={() => {
          void handleDelete();
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
      {children}
    </th>
  );
}

interface TemplateRowProps {
  template: PayPlanTemplate;
  userMap: Map<string, string>;
  onArchive: () => void;
  onUnarchive: () => void;
  onDelete: (template: PayPlanTemplate) => void;
  canArchive: boolean;
  canDelete: boolean;
  canEdit: boolean;
  deleteLoading: boolean;
}

function TemplateRow({
  template,
  userMap,
  onArchive,
  onUnarchive,
  onDelete,
  canArchive,
  canDelete,
  canEdit,
  deleteLoading,
}: TemplateRowProps): React.JSX.Element {
  const isArchived = template.status === 'archived';

  return (
    <tr className={`hover:bg-gray-50 ${isArchived ? 'opacity-60' : ''}`}>
      <td className="px-4 py-3 text-sm font-medium text-gray-900">{template.name}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{template.roleLabel}</td>
      <td className="px-4 py-3 text-sm text-gray-600">{template.department ?? '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {template.payType !== undefined ? PAY_TYPE_LABELS[template.payType] : '—'}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{template.location ?? '—'}</td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {template.grossEntryMode === GrossEntryMode.None
          ? '—'
          : template.baseCommissionRate2 !== undefined
            ? `${template.baseCommissionRate ?? 0}% / ${template.baseCommissionRate2}%`
            : `${template.baseCommissionRate ?? 0}%`}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600 capitalize">
        {GROSS_MODE_LABELS[template.grossEntryMode]}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{template.kickers.length}</td>
      <td className="px-4 py-3">
        <StatusBadge status={template.status} />
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {template.publishedBy !== undefined && template.publishedBy !== '' ? (
          <div>
            <span className="text-gray-700">{formatUserName(template.publishedBy, userMap)}</span>
            <br />
            <span className="text-xs">
              {new Date(template.publishedAt!).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          </div>
        ) : (
          '—'
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          {!canEdit && !canArchive && !canDelete && (
            <span className="text-sm text-gray-400">View only</span>
          )}
          {canEdit && (
            <Link
              to={`/templates/${template.templateId}/edit`}
              className="inline-flex items-center gap-1 text-sm text-brand hover:text-brand-dark"
            >
              <Pencil size={14} />
              Edit
            </Link>
          )}
          {canArchive && !isArchived && (
            <button
              type="button"
              onClick={onArchive}
              className="inline-flex items-center gap-1 text-sm text-yellow-600 hover:text-yellow-700"
            >
              <Archive size={14} />
              Inactive
            </button>
          )}
          {canArchive && isArchived && (
            <button
              type="button"
              onClick={onUnarchive}
              className="inline-flex items-center gap-1 text-sm text-emerald-600 hover:text-emerald-700"
            >
              <ArchiveRestore size={14} />
              Reactivate
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              disabled={deleteLoading}
              onClick={() => onDelete(template)}
              className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700 disabled:opacity-50"
            >
              <Trash2 size={14} />
              Delete
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
